import { useEffect } from "react";
import { Link, useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, CheckCircle2, Hourglass, AlertTriangle, Building2 } from "lucide-react";

interface PublicOrder {
  id: number;
  status: string;
  businessName: string;
  website: string | null;
  industry: string | null;
  ownerEmail: string;
  tenantId: number | null;
  paidAt: string | null;
  provisionedAt: string | null;
  provisioningError: string | null;
  createdAt: string;
}

const STATUS_LABEL: Record<string, { label: string; tone: "info" | "ok" | "warn" | "error" }> = {
  pending: { label: "Awaiting checkout", tone: "info" },
  checkout_started: { label: "Awaiting payment", tone: "info" },
  paid: { label: "Payment confirmed — provisioning your AI workforce", tone: "info" },
  provisioning: { label: "Setting up your AI workforce…", tone: "info" },
  active: { label: "Live", tone: "ok" },
  failed: { label: "Provisioning hit an error", tone: "error" },
  cancelled: { label: "Cancelled", tone: "warn" },
  refunded: { label: "Refunded", tone: "warn" },
};

export default function PlatformOrderStatus() {
  const [, params] = useRoute("/platform/order/:id");
  const id = Number(params?.id);

  const order = useQuery({
    queryKey: ["platform-catalog", "order", id],
    queryFn: async (): Promise<PublicOrder> => {
      const res = await fetch(`/api/platform-catalog/orders/${id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    enabled: Number.isFinite(id) && id > 0,
    // Poll while provisioning is in-flight.
    refetchInterval: ((q: any) => {
      const status: string | undefined = q?.state?.data?.status;
      if (status === "active" || status === "failed" || status === "cancelled" || status === "refunded") return false;
      return 3000;
    }) as any,
  });

  useEffect(() => {
    // Auto-trigger a refetch on mount in case the success_url loaded before
    // the webhook persisted.
    if (order.data && order.data.status === "checkout_started") {
      order.refetch();
    }
  }, [order.data, order]);

  if (!Number.isFinite(id) || id <= 0) {
    return <Centered title="Invalid order" />;
  }

  const status = order.data?.status;
  const detail = status ? STATUS_LABEL[status] : null;
  const Icon = !detail
    ? Hourglass
    : detail.tone === "ok"
      ? CheckCircle2
      : detail.tone === "error"
        ? AlertTriangle
        : Hourglass;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-semibold tracking-tight">AR Dynamics</span>
          </Link>
          <Link href="/platform-services" className="text-sm text-muted-foreground hover:text-foreground">
            All packages
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 space-y-6">
        {order.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !order.data ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">Order not found.</p>
              <Link href="/platform-services">
                <Button variant="outline" className="mt-3">Back to packages</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Icon className={`h-5 w-5 ${detail?.tone === "ok" ? "text-emerald-500" : detail?.tone === "error" ? "text-destructive" : "text-primary"}`} />
                  {detail?.label ?? "Processing"}
                </CardTitle>
                <CardDescription>Order #{order.data.id} · {order.data.businessName}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <Row label="Status">
                  <Badge variant={detail?.tone === "ok" ? "default" : detail?.tone === "error" ? "destructive" : "secondary"} className="capitalize">{order.data.status}</Badge>
                </Row>
                <Row label="Owner email">{order.data.ownerEmail}</Row>
                {order.data.website && <Row label="Website">{order.data.website}</Row>}
                {order.data.industry && <Row label="Industry">{order.data.industry}</Row>}
                {order.data.paidAt && <Row label="Paid at">{new Date(order.data.paidAt).toLocaleString()}</Row>}
                {order.data.provisionedAt && <Row label="Provisioned at">{new Date(order.data.provisionedAt).toLocaleString()}</Row>}
                {order.data.provisioningError && (
                  <div className="bg-destructive/10 text-destructive text-xs p-3 rounded-md">
                    {order.data.provisioningError}
                  </div>
                )}
              </CardContent>
            </Card>

            {order.data.status === "active" && order.data.tenantId && (
              <Card className="border-primary/40">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    Your AI workforce is live
                  </CardTitle>
                  <CardDescription>Tenant #{order.data.tenantId} is now running in the AR Dynamics OS backend.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <p>You'll get a welcome email shortly with portal access and a summary of what was deployed.</p>
                  <p className="text-muted-foreground">
                    The AI employees are already taking calls, answering chats, and updating your CRM.
                  </p>
                </CardContent>
              </Card>
            )}

            {(order.data.status === "paid" || order.data.status === "provisioning") && (
              <Card>
                <CardContent className="p-5 text-sm text-muted-foreground">
                  <p>Hang tight — we're crawling your website, building your Business Brain, and hiring your AI workforce. This usually takes 30-90 seconds.</p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{children}</span>
    </div>
  );
}

function Centered({ title }: { title: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p className="text-muted-foreground">{title}</p>
    </div>
  );
}
